/**
 * This slice is to manage the zones display table
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getLoadedZoneById, updateZoneInActiveAsset } from 'GeminiViewerComponent/features/asset/assetSlice';
import { zoneService } from 'features/services';
import { LoadingStatus, SavingStatus } from 'GeminiViewerComponent/helpers/AsyncStatus';

const sliceName = 'zonesTable';

const initialState = {
    status: LoadingStatus.Idle,

    error: null,
    selectedZoneId: null,

    initialZones: [],
    allZones: [],

    zonePageInfo: {},
    loadedZonesMap: {},
    zoneStatus: LoadingStatus.Idle,

    viewContentPanel: false,
    zoneCategory: [],
    categoryStatus: LoadingStatus.Idle,
};

export const addNewZone = createAsyncThunk(
    `${sliceName}/addNewZone`,
    async (newZone, { getState }) => await zoneService.create(newZone, getState().accounts.activeUser.s3_prefix)
);

export const deleteZoneById = createAsyncThunk(`${sliceName}/a/deleteZoneById`, async (zoneId) => {
    const response = await zoneService.delete(zoneId);
    response.id = zoneId;
    return response;
});

export const deleteZoneArray = createAsyncThunk(`${sliceName}/a/deleteZoneArray`, async ({ assetId, zoneIds }) => {
    const response = await zoneService.deleteArray(zoneIds);
    response.asset_id = assetId;
    response.ids = zoneIds;
    return response;
});

export const duplicateZone = createAsyncThunk(
    `${sliceName}/a/duplicateZone`,
    async (existingZoneId, { getState }) =>
        await zoneService.duplicate(existingZoneId, getState().accounts.activeUser.s3_prefix)
);

export const editZone = createAsyncThunk(
    `${sliceName}/a/editZone`,
    async (props, { getState }) => await zoneService.update(props, getState().accounts.activeUser.s3_prefix)
);

export const pinZone = createAsyncThunk(`${sliceName}/pinZone`, async (zoneId, { getState, dispatch }) => {
    // Reverse current pinned state
    const zone = getLoadedZoneById(getState(), zoneId);
    const params = { object_id: zoneId, is_pinned: !zone.is_pinned };
    var response = await zoneService.zonePinning(params, getState().accounts.activeUser.s3_prefix);
    if (response) {
        response.pinId = zoneId;
        await dispatch(
            updateZoneInActiveAsset({
                zone_id: zoneId,
                is_pinned: !response.payload.is_pinned,
            })
        );
    }
    return response;
});

export const fetchZonesRange = createAsyncThunk(`${sliceName}/fetchZonesRange`, async (params, { getState }) => {
    const response = await zoneService.getAll(
        params.assetId,
        params.includeItems,
        params.includeChildren ?? true,
        getState().accounts.activeUser.s3_prefix
    );
    return response;
});

export const fetchAllZones = createAsyncThunk(`${sliceName}/fetchAllZones`, async (params, { getState }) => {
    const response = await zoneService.getAll(
        params.assetId,
        params.includeItems,
        params.includeChildren ?? true,
        getState().accounts.activeUser.s3_prefix
    );
    return response;
});

export const fetchMoreZones = createAsyncThunk(`${sliceName}/fetchMoreZones`, async (params, { getState }) => {
    const response = await zoneService.getRange(
        params.searchString,
        params.assetId,
        params.startIndex,
        params.stopIndex,
        params.sort,
        getState().accounts.activeUser.s3_prefix,
        true,
        params.includeItems
    );
    return response;
});

export const copyZoneToAsset = createAsyncThunk(`${sliceName}/copyZoneToAsset`, async (params) => {
    const response = await zoneService.copyZoneToAsset(params.zone_ids, params.asset_ids);
    return response;
});

// ---------------------------- ZONE CATEGORY --------------------------------
export const fetchZoneCategory = createAsyncThunk(`${sliceName}/fetchZoneCategory`, async ({ asset_id }) => {
    const response = await zoneService.getZoneCategoryByAssetId(asset_id);
    return response;
});

export const createZoneCategory = createAsyncThunk(
    `${sliceName}/createZoneCategory`,
    async (params) => await zoneService.createZoneCategory(params)
);

export const updateZoneCategory = createAsyncThunk(
    `${sliceName}/updateZoneCategory`,
    async (params) => await zoneService.updateZoneCategory(params)
);

export const updateMultipleZoneCategory = createAsyncThunk(
    `${sliceName}/updateMultipleZoneCategory`,
    async (params) => await zoneService.updateMultipleZoneCategory(params)
);

export const deleteZoneCategory = createAsyncThunk(
    `${sliceName}/deleteZoneCategory`,
    async (zoneId) => await zoneService.deleteZoneCategory(zoneId)
);
// ---------------------------- END ZONE CATEGORY ----------------------------

export const exportAssetDetails = createAsyncThunk(
    `${sliceName}/exportAssetDetails`,
    async (params) =>
        await zoneService.exportAssetDetails(
            params.startIndex ?? 0,
            params.stopIndex ?? 0,
            params.assetId ?? null,
            params.searchString,
            params.sort,
            params.fill_all_fields ?? null,
            params.include_header ?? null,
            params.include_items ?? null,
            params.include_links ?? null,
            params.include_nav_links ?? null,
            params.output_fields
        )
);

const zonesTableSlice = createSlice({
    name: sliceName,
    initialState,
    reducers: {
        clearSelectedZone: (state) => {
            state.selectedZoneId = null;
        },
        resetTableZones: (state) => {
            return initialState;
        },
        setTableZoneStatus: (state, action) => {
            state.zoneStatus = action.payload;
        },
        setSelectedZone: (state, action) => {
            state.selectedZoneId = action.payload;
        },
        updateTableZone: (state, action) => {
            let zoneIdx = state.allZones.findIndex((zone) => zone.zone_id === action.payload.zone_id);
            if (zoneIdx !== -1) {
                state.allZones[zoneIdx] = {
                    ...state.allZones[zoneIdx],
                    ...action.payload,
                    init_view_pitch: action.payload.init_view_pitch ?? undefined,
                    init_view_roll: action.payload.init_view_roll ?? undefined,
                    init_view_yaw: action.payload.init_view_yaw ?? undefined,
                    init_view_fov: action.payload.init_view_fov ?? undefined,
                    init_view_flat_x: action.payload.init_view_flat_x ?? undefined,
                    init_view_flat_y: action.payload.init_view_flat_y ?? undefined,
                };
            }
        },
        deletedAssetZone: (state, action) => {
            const zoneAsset = state.assets.find((x) => x.asset_id === action.payload.assetId);
            if (zoneAsset) {
                zoneAsset.zone_count -= 1;
            }
            state.selectedZoneId = null;
            state.zoneStatus = LoadingStatus.Idle;
            state.loadedZonesMap = {};
        },
        addedAssetZone: (state) => {
            return initialState;
        },
        editedAssetZone: (state) => {
            return initialState;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchZonesRange.pending, (state) => {
                state.zoneStatus = LoadingStatus.Loading;
            })
            .addCase(fetchZonesRange.fulfilled, (state, action) => {
                state.zoneStatus = LoadingStatus.Loaded;
                state.zonePageInfo = action.payload.pageInfo;
                state.initialZones = action.payload.zones;
                state.allZones = action.payload.zones;

                state.loadedZonesMap = {};

                for (var i = 0; i < action.payload.zones.length; i++) {
                    state.loadedZonesMap[i] = LoadingStatus.Loaded;
                }
            })
            .addCase(fetchZonesRange.rejected, (state, action) => {
                state.error = action.error.message;
            })
            .addCase(fetchAllZones.pending, (state) => {
                state.zoneStatus = LoadingStatus.Loading;
            })
            .addCase(fetchAllZones.fulfilled, (state) => {
                state.zoneStatus = LoadingStatus.Loaded;
            })
            .addCase(fetchAllZones.rejected, (state, action) => {
                state.error = action.error.message;
            })
            .addCase(fetchMoreZones.pending, (state, action) => {
                const startIndex = action.meta.arg.startIndex;
                const stopIndex = action.meta.arg.stopIndex;

                for (var i = startIndex; i <= stopIndex; i++) {
                    state.loadedZonesMap[i] = LoadingStatus.Loading;
                }
            })
            .addCase(fetchMoreZones.fulfilled, (state, action) => {
                const startIndex = action.meta.arg.startIndex;
                const stopIndex = startIndex + action.payload.zones.length - 1;
                const requestedStopIndex = action.meta.arg.stopIndex;

                for (var i = startIndex; i <= stopIndex; i++) {
                    state.loadedZonesMap[i] = LoadingStatus.Loaded;
                }

                // In case we didn't load as many as requested
                for (var j = stopIndex + 1; j <= requestedStopIndex; j++) {
                    delete state.loadedZonesMap[j];
                }

                state.allZones = state.allZones.concat(action.payload.zones);
            })
            .addCase(fetchMoreZones.rejected, (state, action) => {
                const startIndex = action.meta.arg.startIndex;
                const stopIndex = action.meta.arg.stopIndex;

                for (var i = startIndex; i <= stopIndex; i++) {
                    delete state.loadedZonesMap[i];
                }

                state.status = LoadingStatus.Failed;
                state.error = action.error.message;
            })
            .addCase(addNewZone.fulfilled, (state) => {
                return initialState;
            })
            .addCase(deleteZoneById.fulfilled, (state) => {
                return initialState;
            })
            .addCase(deleteZoneArray.fulfilled, (state) => {
                return initialState;
            })
            .addCase(deleteZoneArray.rejected, (state, action) => {
                state.status = SavingStatus.Failed;
                state.error = action.error.message;
            })
            .addCase(duplicateZone.fulfilled, (state) => {
                return initialState;
            })
            .addCase(editZone.fulfilled, (state) => {
                return initialState;
            })
            .addCase(pinZone.fulfilled, (state) => {
                return initialState;
            })
            .addCase(pinZone.rejected, (action) => {
                return action.payload;
            })

            // category
            .addCase(fetchZoneCategory.pending, (state) => {
                state.categoryStatus = LoadingStatus.Loading;
            })
            .addCase(fetchZoneCategory.fulfilled, (state, action) => {
                state.zoneCategory = action.payload;
                state.categoryStatus = LoadingStatus.Loaded;
            })
            .addCase(fetchZoneCategory.rejected, (state) => {
                state.categoryStatus = LoadingStatus.Failed;
            })
            .addCase(createZoneCategory.fulfilled, (state) => {
                state.categoryStatus = LoadingStatus.Idle;
            })
            .addCase(createZoneCategory.rejected, (state, action) => {
                state.categoryStatus = LoadingStatus.Failed;
            })
            .addCase(updateZoneCategory.fulfilled, (state) => {
                state.categoryStatus = LoadingStatus.Idle;
            })
            .addCase(updateZoneCategory.rejected, (state) => {
                state.categoryStatus = LoadingStatus.Failed;
            })
            .addCase(updateMultipleZoneCategory.fulfilled, (state) => {
                state.categoryStatus = LoadingStatus.Idle;
            })
            .addCase(updateMultipleZoneCategory.rejected, (state) => {
                state.categoryStatus = LoadingStatus.Failed;
            })
            .addCase(deleteZoneCategory.fulfilled, (state) => {
                state.categoryStatus = LoadingStatus.Idle;
            })
            .addCase(deleteZoneCategory.rejected, (state) => {
                state.categoryStatus = LoadingStatus.Failed;
            })
            .addCase(copyZoneToAsset.fulfilled, (state, action) => {
                resetTableZones();
            })
            .addCase(copyZoneToAsset.rejected, (state, action) => {
                state.status = SavingStatus.Failed;
                state.error = action.error.message;
            })
            .addCase(exportAssetDetails.fulfilled, (state, action) => {
                if (action?.payload) {
                    const url = window.URL.createObjectURL(new Blob([action.payload]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', 'asset_details.csv');
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                }
            });
    },
});

export const selectSelectedZoneId = (state) => state.zonesTable.selectedZoneId;

export const selectSelectedZone = (state) => {
    return state.zonesTable.allZones.find((zone) => zone.zone_id === state.zonesTable.selectedZoneId);
};

export const selectTableZoneById = (state, zoneId) => {
    return state.zonesTable.allZones ? state.zonesTable.allZones.find((zone) => zone.zone_id === zoneId) : undefined;
};

export const selectSelectedZoneTag = (state) => {
    const zone = selectSelectedZone(state);
    return zone ? zone.content_tag : undefined;
};

export const selectedSelectedZone = (state) => state.assets.displayZone;

export const selectInitialTableZones = (state) => {
    return state.zonesTable.initialZones;
};

export const selectLoadedZonesMap = (state) => state.zonesTable.loadedZonesMap;

export const selectAllTableZones = (state) => state.zonesTable.allZones ?? [];

export const selectZonePageInfo = (state) => state.zonesTable.zonePageInfo;

export const selectZoneCategory = (state) => state.zonesTable.zoneCategory;
export const selectCategoryStatus = (state) => state.zonesTable.categoryStatus;
export const selectTableZoneStatus = (state) => state.zonesTable.zoneStatus;

export const {
    clearSelectedZone,
    setSelectedZone,
    updateTableZone,
    resetTableZones,
    updateZoneItem,
    deleteZoneItemById,
    setTableZoneStatus,
} = zonesTableSlice.actions;

export default zonesTableSlice.reducer;
